import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  feedFromWire,
  type NotificationFeedWire,
} from '@/features/notifications/api/notificationWire'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import type { Notification, NotificationFeed, NotificationGroup } from '@/types/domain'

/* The notification centre (FR-49). The bell is persistent chrome; the panel
 * groups events BY RECORD, because a case with six updates is one thing to
 * read, not six.
 *
 * Founder finance events appear here and on the dashboard only, never by email
 * (FR-50), and the daily email digest is generated from this same feed — so
 * there is one feed resource and no second surface to keep in step.
 *
 * NFR-2 liveness is live: useNotificationStream holds a Server-Sent Events
 * connection and merges arriving events into this query's cache. Nothing here
 * polls — the stream pushes, and a reconnect invalidates so anything missed
 * while the connection was down is recovered by a read.
 *
 * This query remains the source of truth. The stream only ever writes into the
 * cache it fills, so a client with no stream — an old browser, a proxy that
 * strips text/event-stream — still shows a correct feed on every load.
 *
 * There is no fixture branch here any more, and its absence is the point. The
 * bell read `MOCK_NOTIFICATIONS` under VITE_USE_MOCKS while the endpoint behind
 * it was `internal/showcase`'s, whose read-marks were process-local; the real
 * feed (`collab.NotificationHandlers`) replaced that route, and a fixture kept
 * alongside it would be describing a feed nobody serves. Offline UI work now
 * sees this panel's error state rather than eight invented events, which is the
 * honest reading of "there is no server". `useNotificationStream` keeps its own
 * USE_MOCKS guard for a different reason — not "no data" but "no connection to
 * hold open", which is also true in jsdom. */

/** Groups a flat feed by record, newest group first. Sorting is by the most
 *  recent event in each group, NOT by unread count — a group jumping the queue
 *  because something in it went unread would reorder the panel under the
 *  reader's cursor. */
export function groupByRecord(items: Notification[]): NotificationGroup[] {
  const groups = new Map<string, NotificationGroup>()

  for (const item of items) {
    const key = `${item.recordKind}:${item.recordNumber}`
    const group = groups.get(key)

    if (!group) {
      groups.set(key, {
        recordKind: item.recordKind,
        recordNumber: item.recordNumber,
        recordTitle: item.recordTitle,
        href: item.href,
        items: [item],
        unreadCount: item.read ? 0 : 1,
        latestAt: item.at,
      })
      continue
    }

    group.items.push(item)
    if (!item.read) group.unreadCount += 1
    if (item.at > group.latestAt) group.latestAt = item.at
  }

  for (const group of groups.values()) {
    group.items.sort((a, b) => b.at.localeCompare(a.at))
  }

  return [...groups.values()].sort((a, b) => b.latestAt.localeCompare(a.latestAt))
}

export function useNotifications() {
  return useQuery<NotificationFeed>({
    queryKey: queryKeys.notifications.feed(),
    // Mapped on the way in, never rendered raw: the server's `kind` is an outbox
    // topic and the panel's is a closed union of eight looks. See
    // notificationWire.ts.
    queryFn: async () =>
      feedFromWire(await api.get<NotificationFeedWire>('/notifications')),
  })
}

/** Marks one notification, or the whole feed, as read.
 *
 *  The cache is updated optimistically: marking read is a reversible,
 *  low-stakes act, and waiting a round-trip to dim a row makes the panel feel
 *  broken. On failure the previous feed is restored. */
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    // `{}` rather than an omitted body for mark-all: the server reads an absent
    // id as "everything", and an empty object is the shape its handler
    // documents for the button that has no one row in mind.
    mutationFn: (id?: string) =>
      api.post<void>('/notifications/read', id ? { id } : {}),

    onMutate: async (id) => {
      const key = queryKeys.notifications.feed()
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<NotificationFeed>(key)

      queryClient.setQueryData<NotificationFeed>(key, (feed) => {
        if (!feed) return feed
        const items = feed.items.map((item) =>
          id === undefined || item.id === id ? { ...item, read: true } : item,
        )
        return { items, unreadCount: items.filter((item) => !item.read).length }
      })

      return { previous }
    },

    onError: (_error, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.notifications.feed(), context.previous)
      }
    },

    // Reconcile against the server once the dust settles. Unconditional now
    // that the feed has no fixture branch: the read-mark is a durable row, so a
    // refetch confirms the optimistic update rather than undoing it.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all })
    },
  })
}
